(ns frontend.view.task.webentry-resolution.components
  (:require
   [basic-components.collapsible-panel :as panel]
   [clojure.set :as set]
   [clojure.string :as string]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.field :as field]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [frontend.state.current-session :as session]
   [frontend.tenant-customizations :as tenant-customizations]
   [frontend.utils :as fu]
   [frontend.view.task.webentry-resolution.events :as events]
   [potpuri.core :as potpuri]
   [reagent.core :as r]
   [ui.components.alert.view :as alert]
   [ui.components.button.view :as button]
   [ui.components.input.checkbox :as checkbox]
   [ui.dom-globals :as dg]
   [ui.editors.labelled-input.view :as labelled-input]
   [ui.util :as uu]))

(defn- id->option
  [v]
  (cond
    (map? v)    v
    (number? v) {:value v :label (entity/display-string (session/get-entity v))}
    :else       nil))

(defn ids->options
  "Open-search wants `{:value :label}` option maps; draft-state holds raw ids."
  [data-type value]
  (if (= data-type :data.type/ref-multi)
    (->> value (keep id->option) vec)
    (id->option value)))

(defn options->ids
  "Inverse of `ids->options` for open-search on-change values."
  [data-type opt]
  (let [->id (fn [o] (if (map? o) (:value o) o))]
    (if (= data-type :data.type/ref-multi)
      (mapv ->id opt)
      (->id opt))))

(defn creator-field
  "Renders one webentry-resolution creator field on the new input system; every
   field validates and writes to the local draft on change (nothing persists
   until the create button). `validate!` is (fn [attr value]) -> error (and
   writes the field's error/live state); `save!` is (fn [attr value]);
   `set-error!` is (fn [attr error]); `containing-entity` is the draft, used to
   scope option lists. `override-args` is merged last, letting a caller tweak a
   field's options/value/filtering (e.g. deal's pipeline + target-asset
   fields)."
  [{:keys [attribute field-schema value label mandatory? error containing-entity
           validate! save! set-error! secondary-args override-args]}]
  (let [{:field/keys [data-type not-clearable?]} field-schema
        editor-type  (:editor/type field-schema)
        search?      (= editor-type :editor.type/search)
        currency?    (= data-type :data.type/currency-value)
        multi?       (contains? #{:data.type/ref-multi :data.type/keyword-multi} data-type)
        select-like? (and (not search?)
                          (or (contains? #{:editor.type/select :editor.type/multi-select} editor-type)
                              (contains? #{:data.type/keyword :data.type/keyword-multi
                                           :data.type/ref-single :data.type/ref-multi} data-type)))
        cleared?     (fn [v]
                       (or (nil? v)
                           (and (string? v) (string/blank? v))
                           (and (coll? v) (empty? v))))
        commit!      (fn [v]
                       (if (and (cleared? v) (or not-clearable? mandatory?))
                         (set-error! attribute (loc/tr :value-cannot-be-cleared))
                         (when-not (validate! attribute v)
                           (save! attribute v))))
        args         (cond-> {:label       label
                              :mandatory?  mandatory?
                              :placeholder (field/placeholder field-schema)
                              :error       error
                              :data-type   data-type
                              :editor-type editor-type
                              :value       value
                              :on-change   (fn [v & _] (commit! v))}
                       ;; selects/refs: resolved option list + concrete editor-type
                       select-like?
                       (assoc :editor-type (or editor-type
                                               (if multi? :editor.type/multi-select :editor.type/select))
                              :options (fu/field-schema-options
                                        field-schema
                                        {:containing-entity containing-entity}))
                       ;; open-search exchanges {:value :label} maps, not raw ids
                       search?
                       (merge (select-keys field-schema [:field/entity-types :field/select-entity-types
                                                         :named-value-attribute :field/not-clearable?])
                              {:value     (ids->options data-type value)
                               :on-change (fn [opt & _] (commit! (options->ids data-type opt)))})
                       ;; currency value renders as a plain number paired with its symbol
                       currency?
                       (assoc :editor-type    nil
                              :secondary-args (merge secondary-args
                                                     {:data-type :data.type/currency-symbol
                                                      :on-change (fn [v & _]
                                                                   (save! (:attribute secondary-args) v))}))
                       ;; caller-supplied per-field tweaks win
                       (seq override-args)
                       (merge override-args))]
    [:div
     (if currency?
       [labelled-input/currency-view args]
       [labelled-input/view args])
     (when error
       [:p
        {:class "text-danger-light-text text-xs"}
        error])]))

(defn processing
  [_]
  [:div
   {:class "max-w-[1024px] mx-auto mt-4"}
   [:div
    {:class "border border-solid border-base-light-border-paler rounded"}
    [:div
     {:class "p-3 border-b border-solid border-base-light-border-paler"}
     [:div (loc/tr :processing)]]
    [:div
     {:class "p-3"}
     [:i.fal.fa-spinner.fa-pulse.fa-3x]]]])

(defn footer-toolbar
  [{:keys [task current-view resolved-asset-data submitted-asset] :as signals}]
  (let [current-view @current-view
        {:keys [task/completed?]} @task
        show-skip? (case current-view
                     :asset (:show-skip? (events/asset-view-info signals))
                     :contacts (:show-skip? (events/contacts-view-info signals))
                     false)
        asset-resolved? (number? (:asset-id @resolved-asset-data))
        no-asset-submitted? (empty @submitted-asset)
        show-complete-button? (and (= current-view :deal)
                                   (or asset-resolved?
                                       no-asset-submitted?)
                                   (not completed?))
        ;;if contacts-resolved => show complete-task-button
        ;; how to decide if contacts are resolved?
        disable-continue? show-skip?]
    [:div
     {:class-name (fu/cs "py-3 px-5 flex min-h-3 min-h-5 flex items-center justify-between gap-4 fixed bottom-0 left-0 right-0 bg-base-light border-t border-solid border-base-light-border-paler")}
     (when-not (= current-view :asset)
       [:div
        [button/secondary-hollow
         {:on-click (fn [_]
                      (events/go-back signals))}
         (loc/tr :go-back)]])
     [:div
      {:class "ml-auto flex gap-3"}
      [:div
       {:class "flex gap-3"}
       (when show-skip?
         [button/danger-solid
          {:on-click (fn [_]
                       (events/skip-step signals))}
          (loc/tr :skip-this-step)])
       (when-not (= current-view :deal)
         [button/primary-solid
          {:disabled? disable-continue?
           :on-click  (fn [_]
                        (when-not disable-continue?
                          (events/continue signals)))}
          (loc/tr :continue)])
       (when show-complete-button?
         [button/primary-solid
          {:on-click (fn [_]
                       (events/complete-task! signals))}
          (loc/tr :mark-as-complete)])
       (when completed?
         [button/primary-solid
          {:on-click (fn [_]
                       (events/reactivate-task! signals))}
          (loc/tr :reactivate-this-task)])]]]))

(defn state-indicator
  [{:keys [current-view asset-type completed?] :as _signals}]
  (let [current-view @current-view
        asset-type @asset-type
        completed? @completed?
        asset-type-name (->> (loc/tr asset-type)
                             str
                             string/lower-case)]
    [:div
     {:class "flex items-center min-w-[800px] lg:min-w-[1040px] text-xs text-base-light-text font-medium sm:text-base"}
     [:div
      {:class-name (uu/cm "w-full relative text-base-light-text after:content-['']  after:w-full after:h-0.5  after:bg-base-regular after:inline-block after:absolute after:top-3 after:left-2/4 last:after:content-none "
                          (when (some #{current-view} [:contacts :deal])
                            "done")
                          (when (= current-view :asset)
                            "active"))}
      [:div
       {:class "relative block text-center z-10"}
       [:span
        {:class "relative inline-flex h-6 w-6 items-center justify-center rounded-full"}
        [:span {:class
                (uu/cm "h-5 w-5 rounded-full bg-base-regular flex items-center justify-center "
                       (when (some #{current-view} [:contacts :deal]) "bg-primary-regular")
                       (when (= current-view :asset)
                         "bg-primary-regular"))}
         [:i {:class (uu/cm "fas "
                            (when (some #{current-view} [:contacts :deal]) "fa-check text-primary-regular-text")
                            (when (= current-view :asset)
                              "fa-circle text-secondary-regular-text"))}]]]
       [:div
        {:class "flex flex-col gap-1"}
        [:div
         {:class "m-0 pt-1.5 font-semibold leading-3 text-sm"}
         (loc/tr :review-asset-type-info [asset-type-name])]]]]

     [:div
      {:class-name (uu/cm "w-full relative text-base-light-text after:content-['']  after:w-full after:h-0.5  after:bg-base-regular after:inline-block after:absolute after:top-3 after:left-2/4 last:after:content-none "
                          (when (some #{current-view} [:contacts :deal])
                            "done")
                          (when (= current-view :asset)
                            "active"))}
      [:div
       {:class "relative block text-center z-10"}
       [:span
        {:class "relative inline-flex h-6 w-6 items-center justify-center rounded-full"}
        [:span {:class (uu/cm "h-5 w-5 rounded-full bg-base-regular flex items-center justify-center"
                              (when (= current-view :deal) "bg-primary-regular")
                              (when (= current-view :contacts)
                                "bg-primary-regular"))}
         [:i {:class (uu/cm "fas "
                            (when (= current-view :deal) "fa-check text-primary-regular-text")
                            (when (= current-view :contacts)
                              "fa-circle text-secondary-regular-text"))}]]]
       [:div
        {:class "flex flex-col gap-1"}
        [:div
         {:class "m-0 pt-1.5 font-semibold leading-3 text-sm"}
         (loc/tr :review-contacts)]]]]

     [:div
      {:class-name (uu/cm "w-full relative text-base-light-text after:content-[''] after:w-full after:h-0.5 after:bg-base-regular after:inline-block after:absolute after:top-3 after:left-2/4 last:after:content-none"
                          (when (and (= current-view :deal) completed?) "done")
                          (when (and (= current-view :deal) (not completed?)) "active"))}
      [:div
       {:class "relative block text-center z-10"}
       [:span
        {:class "relative inline-flex h-6 w-6 items-center justify-center rounded-full"}
        [:span {:class (uu/cm "h-5 w-5 rounded-full bg-base-regular flex items-center justify-center"
                              (when (= current-view :deal) "bg-primary-regular"))}
         (when (= current-view :deal)
           [:i {:class (uu/cm "fas"
                              (if completed?
                                "fa-check text-primary-regular-text"
                                "fa-circle text-secondary-regular-text"))}])]]
       [:div
        {:class "flex flex-col gap-1"}
        [:div {:class "m-0 pt-1.5 font-semibold leading-3 text-sm"}
         (loc/tr :review-deal-info) [:span.text-xs (loc/tr :optional--lower-case)]]
        ]]]]))

(defn sort-by-mandatory-field-first
  [mandatory-fields k]
  (let [tenant-schema @session/tenant-schema]
    (or (->> mandatory-fields
             (map-indexed (fn [idx mk]
                            (when (= mk k)
                              idx)))
             (remove nil?)
             (first))
        (->> k
             (registry/get-field-schema tenant-schema)
             :field/default-ord))))

(defonce asset-attributes [:asset/name])
(defonce company-create-attributes
  (concat asset-attributes [] [:company/website :asset/description :company/sector]))

;;TODO can be removed?
(defn get-fields
  [entity-type schema mandatory-fields]
  (case entity-type
    :entity.type/company
    [(potpuri/deep-merge
      schema
      {:asset/name {:field/default-visibility {:editor :editor.type/text-simple}}})
     (->> (set/union
           (set company-create-attributes)
           (when (tenant-customizations/diamond-edge-ventures?)
             (set [:asset/country :common/network-drive]))
           (set mandatory-fields))
          (into [])
          (sort-by (partial sort-by-mandatory-field-first mandatory-fields)))]))

(defn truncated-text
  [_text _limit start-expanded?]
  (let [local-state (r/atom {:show-all? start-expanded?})]
    (fn [text limit]
      (let [text (str text)
            length (count text)
            {:keys [show-all?]} @local-state
            truncated (cu/truncate limit text)]
        [:div
         [:span
          {:class "block w-full"}
          (if show-all?
            text
            truncated)]
         (when (and text
                    (> length limit))
           [:span
            {:class "pt-2 inline-block text-underline cursor-pointer"
             :on-click (fn [e]
                         (dg/stop-propagation e)
                         (swap! local-state update :show-all? not))}
            (loc/tr (if show-all?
                      :show-less
                      :show-all))])]))))

(defn message-info
  [message]
  [alert/info
   {}
   [:h3
    {:class "font-semibold"}
    (loc/tr :notes-from-submitter)]
   [:div
    [truncated-text message 50 false]]])

(defn select-attachment-view
  [attachment selected? toggle-attachment!]
  (let [{:keys [attachment/filename db/id]} attachment]
    [:div
     {:class "flex items-center gap-3"}
     [checkbox/view
      {:value      selected?
       :on-change (fn [_e checked?]
                     (toggle-attachment! id checked?))}]
     [:div
      {:class "flex justify-between items-center gap-3"}
      [:div
       {:class "flex items-center gap-2 flex-1"}
       [:i.fal.fa-paperclip]
       [:span
        {:title filename
         :class "truncate"}
        filename]]
      [:div
       [button/square-secondary-hollow
        {:class    "cursor-pointer"
         :tooltip  (loc/tr :download)
         :on-click (fn [_e]
                     (fu/open-attachment! (:db/id attachment)))}
        [:i.fal.fa-download]]]]]))

(defn attachment-selector
  [state _attachments]
  (let [toggle-attachment! (fn [id]
                             (let [selected (set (:selected @state))
                                   selected? (contains? selected id)
                                   new-selected (if-not selected?
                                                  (conj selected id)
                                                  (disj selected id))]
                               (swap! state assoc :selected new-selected)))]
    (fn [state attachments]
      (let [selected (set (:selected @state))]
        [:div
         {:class "flex flex-col gap-2"}
         (for [attachment attachments
               :let [{:keys [db/id]} attachment]]
           ^{:key (str "selectable-attachment-" id)}
           [select-attachment-view attachment (contains? selected id) toggle-attachment!])]))))

(defn attachment-view
  [_signals attachment]
  (let [{:keys [attachment/filename]} attachment]
    [:div
     {:class "max-w-full flex justify-between items-center gap-3"}
     [:div
      {:class "w-10/12 flex items-center gap-2 flex-auto"}
      [:i.fal.fa-paperclip]
      [:span
       {:title filename
        :class "inline-block truncate flex-1"}                                                      ;;<- full filename on hover
       filename]]
     [:div
      {:class "flex-1"}
      [button/square-secondary-hollow
       {:tooltip  (loc/tr :download)
        :on-click (fn [_e]
                    (fu/open-attachment! (:db/id attachment)))}
       [:i.fal.fa-download]]]]))

(defn attachments-section
  [{:keys [state] :as signals} kw title attachments]
  [:div
   {:class "my-4"}
   [panel/view
    {:state       state
     :start-open? true
     :panel-key   kw}
    title
    [:div
     {:class "flex flex-col gap-2"}
     (doall
      (for [attachment attachments]
        ^{:key (str "webentry-attachment-" kw (:db/id attachment))}
        [attachment-view signals attachment]))]]])

(defn notification-view
  [{:keys [notification] :as signals}]
  (let [{:keys [title text visible?]} @notification]
    [:div
     {:class (uu/cm "fixed top-[100px] left-2/4 -translate-y-2/4 z-50 bg-base-light opacity-0 transition-all"
                    (when-not visible?
                      "opacity-0 transition-all"))}
     [alert/info
      {}
      ;; If necessary, pass icon as a parameter:
      [:div
       {:class "flex flex-col gap-2"}
       [:div
        {:class "font-bold"}
        title]
       [:div
        {:class "m-0 text-sm"}
        text]]
      [:div
       {:class    "absolute top-2 right-2 text-xl cursor-pointer"
        :on-click (fn [_]
                    (events/hide-notification! signals))}
       [:i.fal.fa-times]]]]))
